// Scorecard metrics, computed by replaying a command log (PLAN §10).
//
// Derived rather than recorded: a log replays to exactly one sequence of
// states, so every metric here is reproducible from the log alone. That is
// what lets the deployment collect playtest data for free — every game leaves
// a log, and the numbers come out later.
//
// Rows the prototype cannot answer yet (win rates against a pure-chess
// baseline, whether the board is *readable*) are deliberately absent rather
// than faked. They need an AI opponent and a human respectively.
//
// **The row numbers below are the ones in PLAN §10, and they were wrong.**
// Five of the six blocks cited a different row than the one they measure —
// deflation was labelled row 6 when row 6 is counterplay, the tech ladder was
// labelled row 5 when row 5 is knight re-aim, and one block covered two rows
// under one heading. Nothing broke, because nothing read them: this file was
// 177 lines imported by no code and no test, which is how a comment stays
// wrong. `scorecard.test.ts` now pins the mapping, so the next person to
// renumber a row has to renumber it here too.
//
// What is still **not** covered, named rather than quietly missing:
// row 1 (needs a pure-chess baseline opponent), row 2 (needs a person),
// row 4 (mean lane length, foundries off the back rank — computable, just not
// computed), row 9 (needs win/loss over many games), row 12 (needs wall-clock
// timing, which a replay does not have).

import {
  type Color,
  FORWARD_FACING,
  PAWN,
  pieceColor,
  pieceType
} from "src/engine/rules/board";
import {
  applyCommand,
  type Command,
  type GameState,
  newGame
} from "src/engine/rules/commands";
import { beltDirAt, NO_BELT, topCargoGrade } from "src/engine/rules/industry/state";
import { facingAt, pieceAt } from "src/engine/rules/position";
import { PIECE_VALUE } from "src/engine/rules/rules";

export interface Scorecard {
  plies: number;
  status: GameState["status"];

  /** **Row 8** — deflation must hold: material may only fall. */
  materialStart: number;
  materialEnd: number;
  deflationHeld: boolean;

  /** **Row 7** — does the tech ladder get climbed? `minted` also feeds row 10. */
  gradesReached: number;
  minted: number;

  /** **Row 6** — is counterplay priced? (Row 2 is readability, which needs a person.) */
  beltsLaid: number;
  beltsCrushed: number;
  /**
   * Mean plies a belt part survived, or **null when none was crushed**.
   *
   * Null rather than `Infinity`, which is what it was, and which was wrong
   * three ways at once. Row 6's threshold is *below* twenty turns, and
   * `Infinity < 20` is false — so a game with no factory in it at all scored
   * as a **failure** of "is counterplay priced", which is a different claim
   * from "parts live too long". Averaged over a cohort, one such game makes
   * the whole cohort Infinity. And `JSON.stringify` turns Infinity into
   * `null` silently, so a telemetry file on disk had already lost the
   * distinction — which is how this survived: every probe of it printed
   * `null` and looked correct.
   */
  meanBeltLifespan: number | null;

  /** **Row 5** — does knight re-aim get used? */
  reAims: number;
  /** **Row 3** — does pawn structure survive facing? */
  pawnsFacingForwardAtEnd: number;
  pawnsAtEnd: number;


  /** Not a scorecard row. Kept because "is anybody digging at all" is the
   *  first question asked of a log that looks wrong. */
  mined: number;
}

const materialOf = (state: GameState): number => {
  let total = 0;
  for (const sq of state.position.cellList) {
    const piece = pieceAt(state.position, sq);
    if (piece) total += PIECE_VALUE[pieceType(piece)];
  }
  return total;
};

const beltCount = (state: GameState): number =>
  state.position.cellList.reduce((n, sq) => n + (beltDirAt(state.industry, sq) !== NO_BELT ? 1 : 0), 0);

/**
 * Replay a log and accumulate the metrics. Deliberately takes the log rather
 * than a live store: the same function serves a finished game on disk, a game
 * in progress, and a tuning sweep over many logs.
 */
export function scoreLog(log: readonly Command[]): Scorecard {
  let state = newGame();
  const materialStart = materialOf(state);

  let minted = 0;
  let mined = 0;
  let gradesReached = 0;
  let beltsLaid = 0;
  let beltsCrushed = 0;
  let reAims = 0;
  /** Sum of how long each belt survived, in plies, for the lifespan mean. */
  let beltLifeTotal = 0;
  let ply = 0;
  const laidAt = new Map<number, number>();

  for (const command of log) {
    const before = state;
    const beltsBefore = beltCount(before);

    state = applyCommand(state, command);

    switch (command.type) {
      case "lay-belt":
        beltsLaid++;
        laidAt.set(command.square, ply);
        break;
      case "re-aim":
        reAims++;
        break;
    }

    const tick = state.lastTick;
    if (tick) {
      minted += tick.minted.length;
      mined += tick.mined;
    }

    for (const sq of state.position.cellList) {
      const grade = topCargoGrade(state.industry, sq);
      if (grade > gradesReached) gradesReached = grade;
    }

    // A belt that vanished across a move was crushed (demolition is its own
    // command, so it does not land here).
    if (command.type === "move" && beltCount(state) < beltsBefore) {
      beltsCrushed += beltsBefore - beltCount(state);
      for (const sq of state.position.cellList) {
        if (beltDirAt(before.industry, sq) !== NO_BELT && beltDirAt(state.industry, sq) === NO_BELT) {
          beltLifeTotal += ply - (laidAt.get(sq) ?? 0);
          laidAt.delete(sq);
        }
      }
    }

    if (command.type === "move" || command.type === "re-aim") ply++;
  }

  let pawnsAtEnd = 0;
  let pawnsFacingForwardAtEnd = 0;
  for (const sq of state.position.cellList) {
    const piece = pieceAt(state.position, sq);
    if (!piece || pieceType(piece) !== PAWN) continue;
    pawnsAtEnd++;
    if (facingAt(state.position, sq) === FORWARD_FACING[pieceColor(piece) as Color]) {
      pawnsFacingForwardAtEnd++;
    }
  }

  const materialEnd = materialOf(state);

  return {
    plies: ply,
    status: state.status,
    materialStart,
    materialEnd,
    deflationHeld: materialEnd <= materialStart,
    gradesReached,
    minted,
    beltsLaid,
    beltsCrushed,
    meanBeltLifespan: beltsCrushed > 0 ? beltLifeTotal / beltsCrushed : null,
    reAims,
    pawnsFacingForwardAtEnd,
    pawnsAtEnd,
    mined
  };
}
